import { useCallback, useEffect, useRef } from 'react';
import { CloseIcon } from '../../icons/CloseIcon';
import { CloseIconSlot } from '../../icons/CloseIconSlot';
import {
  Body,
  CloseButton,
  CloseRow,
  Container,
  Footer,
  Overlay,
  Panel,
  Title,
  TitleRow,
} from './Modal.styles';
import type { ModalProps } from './Modal.types';

const FOCUSABLE_SELECTOR =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  ariaLabel,
  labels = {},
  aboveMobileNav = false,
  disableBodyPadding = false,
  size = 'md',
}: ModalProps) {
  const { close = 'Close' } = labels;
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.setAttribute('data-voyager-lock-scroll', '');
    document.body.style.paddingRight = `${scrollbarWidth}px`;
    const style = document.createElement('style');
    style.textContent =
      'body[data-voyager-lock-scroll]{overflow:hidden;touch-action:none;overscroll-behavior:none}';
    document.head.appendChild(style);

    panelRef.current?.focus({ preventScroll: true });

    return () => {
      document.body.removeAttribute('data-voyager-lock-scroll');
      document.body.style.removeProperty('padding-right');
      style.remove();
    };
  }, [open]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      if (e.key !== 'Tab' || !panelRef.current) return;

      const focusable = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      } else if (!panelRef.current.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return;

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, handleKeyDown]);

  if (!open) return null;

  return (
    <Container
      $open={open}
      $aboveMobileNav={aboveMobileNav}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel || title || 'Dialog'}
    >
      <Overlay $open={open} onClick={onClose} />
      <Panel
        $open={open}
        $aboveMobileNav={aboveMobileNav}
        $size={size}
        ref={panelRef}
        tabIndex={-1}
      >
        {title && (
          <>
            <CloseRow>
              <CloseButton onClick={onClose} aria-label={close} type="button">
                <CloseIconSlot>
                  <CloseIcon size={10} />
                </CloseIconSlot>
              </CloseButton>
            </CloseRow>
            <TitleRow>
              <Title>{title}</Title>
            </TitleRow>
          </>
        )}
        <Body $noPadding={disableBodyPadding}>{children}</Body>
        {footer && <Footer>{footer}</Footer>}
      </Panel>
    </Container>
  );
}
